// ArtifactPanel.tsx — hosts one artifact in the viewer its mime type picks
// (registry.ts): loads the viewer's chunk, downloads the bytes through
// increment's /content proxy when the viewer wants them, and offers the
// file for download either way.
import { Suspense, lazy, useEffect, useState } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';
import Button from 'react-bootstrap/Button';
import { BsDownload } from 'react-icons/bs';
import { unwrapJson, utils } from 'common';
import { apiClient } from '../api';
import type { Artifact } from '../api';
import { shortSha } from '../pages/format';
import ArtifactDetails from './ArtifactDetails';
import type { CameraSync } from './cameraSync';
import { viewerFor } from './registry';
import type { ArtifactViewerProps } from './registry';

const lazyViewers = new Map<string, LazyExoticComponent<ComponentType<ArtifactViewerProps>>>();

function lazyViewer(id: string, load: () => Promise<{ default: ComponentType<ArtifactViewerProps> }>) {
  let c = lazyViewers.get(id);
  if (!c) {
    c = lazy(load);
    lazyViewers.set(id, c);
  }
  return c;
}

async function fetchBlob(a: Artifact): Promise<Blob> {
  const res = await apiClient.fetch(a.content_url);
  if (!res.ok) {
    // Reuse the shared error unwrapping for the server's {"error": …}.
    await unwrapJson<unknown>(res);
    throw new Error(`HTTP ${res.status}`);
  }
  return res.blob();
}

// saveBlob hands the bytes to the browser as a file named after the
// artifact. The download goes through apiClient so it carries the session
// whether that is a cookie or a stored Bearer token.
function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

interface Props {
  artifact: Artifact;
  sync?: CameraSync | null;
  /** Label above the viewer, e.g. the commit when several are side by side. */
  caption?: string;
}

export default function ArtifactPanel({ artifact, sync, caption }: Props) {
  const plugin = viewerFor(artifact.mime_type);
  const Viewer = lazyViewer(plugin.id, plugin.load);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // The sha256 is the content's identity: a re-upload under the same id
  // refetches, a live-poll re-render of the same row does not.
  useEffect(() => {
    setBlob(null);
    setError('');
    if (!plugin.fetchContent) return;
    let cancelled = false;
    setBusy(true);
    fetchBlob(artifact)
      .then((b) => { if (!cancelled) setBlob(b); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifact.id, artifact.sha256, plugin.fetchContent]);

  const download = async (): Promise<void> => {
    try {
      setBusy(true);
      saveBlob(blob ?? (await fetchBlob(artifact)), artifact.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const waiting = plugin.fetchContent && !blob && !error;
  return (
    <div className="artifact-panel">
      <div className="d-flex flex-wrap align-items-baseline gap-2 mb-2">
        <strong>{caption ?? artifact.name}</strong>
        <span className="text-secondary">
          <code>{shortSha(artifact.commit_sha)}</code> · {artifact.experiment} · {utils.formatBytes(artifact.size)}
        </span>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {waiting ? (
        <div className="text-secondary">Downloading {utils.formatBytes(artifact.size)}…</div>
      ) : (
        <Suspense fallback={<div className="text-secondary">Loading the {plugin.label.toLowerCase()} viewer…</div>}>
          <Viewer artifact={artifact} blob={blob} sync={sync} />
        </Suspense>
      )}
      <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
        <Button variant="outline-secondary" size="sm" onClick={download} disabled={busy}>
          <BsDownload /> Download
        </Button>
        {plugin.id !== 'fallback' && (
          <details className="ms-2">
            <summary className="text-secondary">Details</summary>
            <ArtifactDetails artifact={artifact} />
          </details>
        )}
      </div>
    </div>
  );
}
